import React, { useState, useEffect } from 'react';
import { Package } from 'lucide-react';
import { ToastProvider, useToast } from './core/shell/ToastProvider';

// Komponenten importieren
import LoginView from './components/LoginView';
import Header from './core/shell/Header';
import Footer from './core/shell/Footer';
import CatalogView from './components/CatalogView';
import ProductView from './components/ProductView';
import CartView from './components/CartView';
import KontierungBulkModal from './components/cart/KontierungBulkModal';
import SaveCartModal from './components/cart/SaveCartModal';
import SettingsView from './components/SettingsView';
import AdminView from './components/admin/AdminView';
import ShopEditor from './components/admin/OCIShopManagement/ShopEditor';
import OciFrame from './components/OciFrame';
// BANF-Liste: Tabellen-Ansicht (Design-Handoff 28.07., components/banfList/);
// fachlich abgenommen 29.07., die alte Karten-Ansicht wurde entfernt.
import BanfListView from './components/banfList/BanfListView';
import CreateBanfView from './components/CreateBanfView';
import LogoutConfirmationModal from './components/LogoutConfirmationModal';
import FavoritesView from './components/FavoritesView';
import MissingFieldsModal from './components/MissingFieldsModal';
import HomePage from './components/HomePage';
import ProductDetailView from './components/ProductDetailView';
// WarehouseView: PHASE 0 geparkt (operatives WE-/Bewegungen-Feature, braucht SAP-Prozess) — aus aktivem Routing genommen.
import LagerShell from './components/LagerShell';

// Modul-Register — additiver Einstiegspunkt vor dem bestehenden Switch
import { getModule, hasModuleAccess } from './modules/registry';

// Hooks und Services importieren
import useAuth from 'core/auth/useAuth';
import { apiCall, mockApiResponse, getSavedCarts, createSavedCart, updateSavedCart, deleteSavedCart, getLicense } from 'core/api/apiService';
import { sanitizeQuantity } from 'core/utils/units';
import { ThemeProvider, useTheme } from './core/ui/ThemeContext';
import { LanguageProvider, useLanguage } from './core/i18n/LanguageContext';

const AppContent = () => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  
  // State Management
  const [catalogs, setCatalogs] = useState([]);
  const [selectedCatalog, setSelectedCatalog] = useState(null);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [cart, setCart] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentView, setCurrentView] = useState('login');
  const [loading, setLoading] = useState(false);
  const [adminUsers, setAdminUsers] = useState([]);
  const [ociShops, setOciShops] = useState([]);
  const [internalShops, setInternalShops] = useState([]);
  const [editingShop, setEditingShop] = useState(null);
  const [adminActiveTab, setAdminActiveTab] = useState('general');
  const [globalLogo, setGlobalLogo] = useState(null);
  const [siteTitle, setSiteTitle] = useState('');
  const [ociFrameUrl, setOciFrameUrl] = useState(null);
  const [showOciFrame, setShowOciFrame] = useState(false);
  const [purchaseRequests, setPurchaseRequests] = useState([]);
  const [editingCartItem, setEditingCartItem] = useState(null);
  const [showKontierungModal, setShowKontierungModal] = useState(false);
  const [kontierungPrefill, setKontierungPrefill] = useState({});
  const [editingFavorite, setEditingFavorite] = useState(null); // Favorit zur Bearbeitung
  const [editingDraft, setEditingDraft] = useState(null);
  // Tab-Vorwahl beim Sprung in die BANF-Liste (Katalog-Kachel, Benachrichtigungs-Glocke).
  // Objekt mit Zähler statt String: derselbe Ziel-Tab muss auch beim ZWEITEN Absprung
  // wirken, obwohl sich der Tab-Wert dann nicht ändert (Reviewer cc99d8c1).
  const [banfListNav, setBanfListNav] = useState({ tab: 'all', n: 0 });
  const openBanfList = (tab) => {
    setBanfListNav((v) => ({ tab: typeof tab === 'string' ? tab : 'all', n: v.n + 1 }));
    loadDrafts();
    navigateTo('purchase-requests');
  };
  const [drafts, setDrafts] = useState([]); // Entwürfe für Badge im Header
  const [ociItems, setOciItems] = useState(null); // Array von OCI-Artikeln für CreateBanfView
  const [savedCarts, setSavedCarts] = useState([]); // Gespeicherte Warenkörbe
  const [showSaveCartModal, setShowSaveCartModal] = useState(false); // SaveCartModal
  const [kontierungTargets, setKontierungTargets] = useState([]); // Ziel-Positionen für Kontierung (Auswahl oder alle)
  const [saveCartTargets, setSaveCartTargets] = useState([]); // Ziel-Positionen für Warenkorb speichern (Auswahl oder alle)
  const [lagerWerk, setLagerWerk] = useState(''); // Aktuell gewaehltes Werk fuer Lager-Dashboard

  // Lizenz-State: { licensedModules: Set<string>, mode: 'ok'|'grace' }
  // null = noch nicht geladen (grace-Verhalten in hasModuleAccess)
  const [licenseState, setLicenseState] = useState(null);

  // View-History-Stack fuer echte Schritt-zurueck-Navigation
  const [viewHistory, setViewHistory] = useState([]);

  // OCI-Mode State
  const [ociMode, setOciMode] = useState(false);
  const [ociParams, setOciParams] = useState(null);
  
  // Logout Confirmation Modal State
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  // Missing Fields Modal State (für interne Katalog-Produkte)
  const [showMissingFieldsModal, setShowMissingFieldsModal] = useState(false);
  const [pendingCartProduct, setPendingCartProduct] = useState(null);
  const [missingFields, setMissingFields] = useState([]);
  
  // Toast-Funktionen via ToastProvider (Context)
  const { showSuccess, showError, showWarning, showInfo } = useToast();

  // Navigations-Helfer: Snapshot des aktuellen Zustands pushen, dann View wechseln
  const navigateTo = (view) => {
    setViewHistory(h => [...h, {
      view: currentView,
      selectedCatalog,
      selectedProduct,
      editingDraft
    }]);
    setCurrentView(view);
  };

  // Schritt-zurueck: letzten Snapshot wiederherstellen oder auf Root fallen
  const goBack = () => {
    setViewHistory(h => {
      if (h.length === 0) {
        setCurrentView(ociMode ? 'catalogs' : 'home');
        return h;
      }
      const prev = h[h.length - 1];
      setSelectedCatalog(prev.selectedCatalog);
      setSelectedProduct(prev.selectedProduct);
      setEditingDraft(prev.editingDraft);
      setCurrentView(prev.view);
      return h.slice(0, -1);
    });
  };

  // Custom Hooks
  const { user, isLoading, handleLogin, handleLogout: authLogout, updateUser } = useAuth();
  
  // OCI-Parameter beim App-Start erkennen
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const isOciMode = urlParams.get('oci') === 'true' || urlParams.get('oci_mode') === 'true';
    
    // Debug-Funktionen global verfügbar machen (nur einmal)
    if (!window.activateOCIMode) {
      window.activateOCIMode = (testParams = {}) => {
        console.log('🔧 Manueller OCI-Mode aktiviert!');
        const defaultParams = {
          sessionId: `test_${Date.now()}`,
          username: user?.username || 'test-user',
          hookUrl: 'https://test-sap-system.com/oci-callback',
          target: '_top',
          caller: 'SAP_TEST',
          ...testParams
        };
        setOciMode(true);
        setOciParams(defaultParams);
        
        // Hook-URL auch in Session-Storage speichern
        sessionStorage.setItem('oci_hook_url', defaultParams.hookUrl);
        
        console.log('🔗 Test OCI-Parameter gesetzt:', defaultParams);
      };
      
      window.deactivateOCIMode = () => {
        console.log('🔧 OCI-Mode deaktiviert!');
        setOciMode(false);
        setOciParams(null);
      };
      
      // Debug-Funktionen für Navigation
      window.debugCurrentView = () => {
        console.log('🎯 Current View Debug:', {
          currentView,
          user: user?.username,
          cartLength: cart?.length,
          ociMode,
          ociParams
        });
      };
      
      window.forceNavigateToCart = () => {
        console.log('🛒 Force navigating to cart...');
        setCurrentView('cart');
      };
    }
    
    // OCI-Mode nur setzen wenn er noch nicht aktiv ist
    if (isOciMode && !ociMode) {
      console.log('🔗 OCI-Mode erkannt!');
      
      const hookUrlParam = urlParams.get('oci_hook');
      const ociParameters = {
        sessionId: urlParams.get('oci_session'),
        username: urlParams.get('oci_user'),
        hookUrl: hookUrlParam ? decodeURIComponent(hookUrlParam) : '',
        target: urlParams.get('oci_target') || '_top',
        caller: urlParams.get('oci_caller') || 'SAP'
      };
      
      // Prüfen ob globale OCI-Parameter vorhanden sind
      if (window.OCI_PARAMS) {
        Object.assign(ociParameters, window.OCI_PARAMS);
        console.log('🌐 Globale OCI-Parameter gefunden:', window.OCI_PARAMS);
      }
      
      // Prüfen ob Benutzer bereits als OCI-Benutzer angemeldet ist
      const existingUser = JSON.parse(localStorage.getItem('sap_oci_user') || '{}');
      if (existingUser && existingUser.isOciLogin && existingUser.token) {
        console.log('👤 Bestehender OCI-Benutzer gefunden:', existingUser.username);
        // OCI-Parameter für bereits authentifizierte Benutzer setzen
        ociParameters.username = ociParameters.username || existingUser.username;
        ociParameters.sessionId = ociParameters.sessionId || `existing_${Date.now()}`;
        // Hook-URL aus Session-Storage versuchen zu laden
        if (!ociParameters.hookUrl) {
          const savedHookUrl = sessionStorage.getItem('oci_hook_url');
          if (savedHookUrl) {
            ociParameters.hookUrl = savedHookUrl;
            console.log('🔗 Hook-URL aus Session-Storage geladen:', savedHookUrl);
          }
        }
      }
      
      setOciMode(true);
      setOciParams(ociParameters);
      
      console.log('🔗 OCI-Parameter gesetzt:', ociParameters);
      
      // Hook-URL in Session-Storage speichern für spätere Nutzung
      if (ociParameters.hookUrl) {
        sessionStorage.setItem('oci_hook_url', ociParameters.hookUrl);
      }
      
      // OCI-Token aus URL-Parametern verarbeiten
      const ociToken = urlParams.get('token');
      if (ociToken && !user) {
        console.log('🔐 OCI-Token gefunden, authentifiziere Benutzer...');
        
        try {
          // Token-Payload dekodieren um Benutzerdaten zu extrahieren
          const tokenPayload = JSON.parse(atob(ociToken.split('.')[1]));
          
          const userWithToken = {
            id: tokenPayload.userId,
            username: tokenPayload.username,
            firstName: tokenPayload.firstName,
            lastName: tokenPayload.lastName,
            email: tokenPayload.email,
            name: tokenPayload.username,
            role: tokenPayload.role,
            costCenter: tokenPayload.costCenter,
            department: tokenPayload.department,
            token: ociToken,
            loginTime: Date.now(),
            isOciLogin: true
          };
          
          // Benutzer in localStorage speichern
          localStorage.setItem('sap_oci_user', JSON.stringify(userWithToken));
          
          // Benutzer-State direkt aktualisieren für sofortige Wirkung
          updateUser(userWithToken);
          
          console.log('✅ OCI-Benutzer automatisch authentifiziert:', tokenPayload.username);
          
          // URL-Parameter nach der Authentifizierung entfernen
          const cleanUrl = window.location.pathname + '?oci=true';
          window.history.replaceState({}, document.title, cleanUrl);
          
          // Automatisch zur Katalog-Ansicht wechseln
          setTimeout(() => {
            setCurrentView('catalogs');
            // loadCart wird später automatisch durch die Ansichtsänderung getriggert
          }, 100);
          
        } catch (error) {
          console.error('❌ Fehler beim Verarbeiten des OCI-Tokens:', error);
        }
      }
      
      // Automatisch zur Katalog-Ansicht wechseln wenn Benutzer bereits angemeldet
      if (user && ociParameters.username) {
        setCurrentView('catalogs');
        // loadCart wird durch die Ansichtsänderung automatisch getriggert
      }
    }
  }, []); // Leere Dependency-Array - nur einmal beim Mount ausführen

  // Mobile Detection
  const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           window.innerWidth <= 768;
  };

  // Message-Listener für OCI-Callbacks
  useEffect(() => {
    const handleOCIMessage = (event) => {
      console.log('Received message:', event.data, 'from origin:', event.origin);
      
      // Erweiterte Sicherheitsprüfung für verschiedene Origins
      const allowedOrigins = [
        window.location.origin,
        'https://oci.example.com', // Fügen Sie hier bekannte OCI-Domains hinzu
        'null' // Für lokale Dateien oder data: URLs
      ];
      
      // Bei mobilen Geräten weniger restriktiv sein
      const isValidOrigin = allowedOrigins.includes(event.origin) || 
                           event.origin === window.location.origin ||
                           (isMobile() && event.data && event.data.type && event.data.type.startsWith('OCI_'));

      if (!isValidOrigin) {
        console.log('Message ignored due to origin check:', event.origin);
        return;
      }

      if (event.data.type === 'OCI_CART_UPDATED') {
        console.log('OCI-Artikel erfolgreich übertragen:', event.data);
        
        // OCI-Artikel für CreateBanfView speichern
        if (event.data.ociItems && event.data.ociItems.length > 0) {
          setOciItems(event.data.ociItems);
          console.log('Navigating to CreateBanfView with', event.data.ociItems.length, 'OCI items');
        }
        
        // Bei mobilen Geräten: OCI-Frame schließen
        if (showOciFrame) {
          setShowOciFrame(false);
          setOciFrameUrl(null);
        }
        
        // Zur CreateBanfView wechseln (statt Warenkorb) mit kleiner Verzögerung für bessere UX
        setTimeout(() => {
          setCurrentView('create-banf');
        }, 500);
        
        // Stille Verarbeitung - keine Alert-Popup
        
      } else if (event.data.type === 'OCI_ERROR') {
        console.error('OCI-Fehler:', event.data.error);
        alert(`❌ Fehler bei der OCI-Übertragung: ${event.data.error}`);
        
        // Bei Fehlern: OCI-Frame schließen
        if (showOciFrame) {
          setShowOciFrame(false);
          setOciFrameUrl(null);
        }
      }
    };

    // Event-Listener hinzufügen
    window.addEventListener('message', handleOCIMessage);
    
    // Global reload function für Cart-Updates verfügbar machen
    window.reloadCart = loadCart;
    
    // Hash-Change-Listener für mobile Navigation
    const handleHashChange = () => {
      const hash = window.location.hash.substring(1); // Remove #
      if (hash === '/cart' && user) {
        console.log('Hash navigation to cart detected');
        loadCart();
        setCurrentView('cart');
        // OCI-Frame schließen falls offen
        if (showOciFrame) {
          setShowOciFrame(false);
          setOciFrameUrl(null);
        }
      }
    };
    
    window.addEventListener('hashchange', handleHashChange);

    // Cleanup beim Unmount
    return () => {
      window.removeEventListener('message', handleOCIMessage);
      window.removeEventListener('hashchange', handleHashChange);
      delete window.reloadCart;
    };
  }, [user, showOciFrame]);

  // Favicon dynamisch aktualisieren (nur bei Änderung)
  const [currentFaviconUrl, setCurrentFaviconUrl] = useState(null);
  
  const updateFavicon = (logoUrl) => {
    // Normalisiere logoUrl (null, 'null', '', undefined sollen alle als null behandelt werden)
    const normalizedUrl = (logoUrl === 'null' || logoUrl === null || logoUrl === undefined || logoUrl === '') ? null : logoUrl;
    
    // Prüfe ob sich das Favicon tatsächlich geändert hat
    if (currentFaviconUrl === normalizedUrl) {
      console.log('🎨 Favicon unchanged, skipping update');
      return;
    }
    
    console.log(`🎨 Favicon changed from "${currentFaviconUrl}" to "${normalizedUrl}"`);
    
    // Alle existierenden Favicon-Links entfernen
    const existingFavicons = document.querySelectorAll('link[rel*="icon"]');
    existingFavicons.forEach(favicon => favicon.remove());
    
    if (normalizedUrl) {
      // Neues Favicon mit Logo erstellen
      const favicon = document.createElement('link');
      favicon.rel = 'icon';
      favicon.type = 'image/x-icon';
      favicon.href = normalizedUrl;
      document.head.appendChild(favicon);
      
      // Zusätzlich apple-touch-icon für mobile Geräte
      const appleFavicon = document.createElement('link');
      appleFavicon.rel = 'apple-touch-icon';
      appleFavicon.href = normalizedUrl;
      document.head.appendChild(appleFavicon);
      
      console.log('🎨 Favicon updated with custom logo');
    } else {
      // Standard-Favicon erstellen (Package-Icon als SVG)
      const svgFavicon = `data:image/svg+xml,${encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
          <rect x="0" y="0" width="24" height="24" fill="%23059669" rx="4"/>
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
          <polyline points="3.27,6.96 12,12.01 20.73,6.96"/>
          <line x1="12" y1="22.08" x2="12" y2="12"/>
        </svg>
      `)}`;
      
      const favicon = document.createElement('link');
      favicon.rel = 'icon';
      favicon.type = 'image/svg+xml';
      favicon.href = svgFavicon;
      document.head.appendChild(favicon);
      
      console.log('🎨 Favicon updated with default package icon');
    }
    
    // Aktuellen Favicon-Status merken
    setCurrentFaviconUrl(normalizedUrl);
  };

  // Logo und Titel aktualisieren (optimiert - nur bei Änderung)
  const updateGlobalSettings = async () => {
    try {
      const response = await fetch('/api/public/settings');
      if (response.ok) {
        const settings = await response.json();
        
        // Logo: null, undefined, 'null' oder leerer String sollen alle als null behandelt werden
        const logoValue = (settings.logo === 'null' || settings.logo === null || settings.logo === undefined || settings.logo === '') ? null : settings.logo;
        
        // Nur aktualisieren wenn sich das Logo geändert hat
        if (globalLogo !== logoValue) {
          console.log(`🎨 Logo changed from "${globalLogo}" to "${logoValue}"`);
          setGlobalLogo(logoValue);
          updateFavicon(logoValue);
        }
        
        // Titel nur aktualisieren wenn er sich geändert hat
        if (settings.siteTitle && settings.siteTitle !== siteTitle) {
          console.log(`📄 Site title changed from "${siteTitle}" to "${settings.siteTitle}"`);
          setSiteTitle(settings.siteTitle);
          document.title = settings.siteTitle;
        }
      }
    } catch (error) {
      console.error('Error updating global settings:', error);
    }
  };

  // Periodische Aktualisierung der Einstellungen (nach Initial Load)
  useEffect(() => {
    // Warten bis initiale Einstellungen geladen sind, dann periodisch aktualisieren
    const timer = setTimeout(() => {
      const interval = setInterval(updateGlobalSettings, 30000);
      return () => clearInterval(interval);
    }, 5000); // Nach 5 Sekunden starten
    
    return () => clearTimeout(timer);
  }, []); // Nur einmal starten

  // Event-Listener für sofortige Einstellungsupdates (für Admin-Änderungen)
  useEffect(() => {
    const handleSettingsUpdate = (event) => {
      console.log('🔔 Settings update event received:', event.detail);
      const { logo, siteTitle } = event.detail;
      
      if (logo !== undefined) {
        const logoValue = (logo === 'null' || logo === null || logo === undefined || logo === '') ? null : logo;
        // Nur aktualisieren wenn sich das Logo tatsächlich geändert hat
        if (globalLogo !== logoValue) {
          console.log(`🔔 Event: Logo changed from "${globalLogo}" to "${logoValue}"`);
          setGlobalLogo(logoValue);
          updateFavicon(logoValue);
        }
      }
      
      if (siteTitle && siteTitle !== document.title) {
        console.log(`🔔 Event: Site title changed to "${siteTitle}"`);
        setSiteTitle(siteTitle);
        document.title = siteTitle;
      }
    };
    
    window.addEventListener('settingsUpdated', handleSettingsUpdate);
    return () => window.removeEventListener('settingsUpdated', handleSettingsUpdate);
  }, [globalLogo]); // Abhängigkeit für Logo-Vergleich

  // Logout-Handler der das Bestätigungsmodal anzeigt
  const handleLogoutRequest = () => {
    setShowLogoutModal(true);
  };

  // Eigentlicher Logout nach Bestätigung
  const handleConfirmedLogout = async () => {
    setShowLogoutModal(false);
    const success = await authLogout(cart, ociMode, ociParams);
    if (success) {
      // Alle States zurücksetzen
      setCatalogs([]);
      setSelectedCatalog(null);
      setProducts([]);
      setCart([]);
      setSearchTerm('');
      setOciShops([]);
      setAdminUsers([]);
      setEditingShop(null);
      setPurchaseRequests([]);
      setViewHistory([]); // View-History beim Logout leeren
      setLicenseState(null); // Lizenz-State zuruecksetzen
      setCurrentView('login');
    }
    return success;
  };

  // Handler für Abbruch des Logout-Modals
  const handleLogoutCancel = () => {
    setShowLogoutModal(false);
  };




  // Seitentitel und Einstellungen sofort beim App-Start laden (vor React-Rendering)
  useEffect(() => {
    const loadInitialSettings = async () => {
      try {
        const response = await fetch('/api/public/settings');
        
        if (response.ok) {
          const settings = await response.json();
          
          // Titel sofort setzen
          if (settings.siteTitle) {
            document.title = settings.siteTitle;
            setSiteTitle(settings.siteTitle);
            console.log('Initial site title loaded:', settings.siteTitle);
          } else {
            // Fallback falls kein Titel in DB
            document.title = 'SAP OCI Portal';
            setSiteTitle('SAP OCI Portal');
          }
          
          // Logo sofort setzen
          if (settings.logo) {
            const logoValue = (settings.logo === 'null' || settings.logo === null || settings.logo === undefined || settings.logo === '') ? null : settings.logo;
            setGlobalLogo(logoValue);
            updateFavicon(logoValue);
          }
        } else {
          // Fallback bei API-Fehler
          document.title = 'SAP OCI Portal';
          setSiteTitle('SAP OCI Portal');
        }
      } catch (error) {
        console.error('Failed to load initial settings:', error);
        // Fallback bei Netzwerkfehler
        document.title = 'SAP OCI Portal';
        setSiteTitle('SAP OCI Portal');
      }
    };

    loadInitialSettings();
  }, []); // Läuft nur einmal beim App-Start

  // Seitentitel beim App-Start laden
  const loadSiteTitle = async () => {
    try {
      const response = await fetch('/api/admin/settings', {
        headers: user?.token ? {
          'Authorization': `Bearer ${user.token}`
        } : {}
      });
      
      if (response.ok) {
        const settings = await response.json();
        if (settings.siteTitle) {
          document.title = settings.siteTitle;
          console.log('Site title loaded:', settings.siteTitle);
        }
      }
    } catch (error) {
      console.error('Failed to load site title:', error);
      // Fallback-Titel setzen
      document.title = 'SAP OCI Portal';
    }
  };

  // Warenkorb laden
  const loadCart = async () => {
    try {
      console.log('Loading cart for user:', user?.username);
      const response = await apiCall('/cart', 'GET', null, user);
      if (response && Array.isArray(response)) {
        console.log('Cart loaded successfully:', response.length, 'items');
        setCart(response);
      } else {
        console.warn('Invalid cart response:', response);
        setCart([]);
      }
    } catch (error) {
      console.error('Failed to load cart:', error);
      setCart([]); // Leeren Warenkorb bei Fehler setzen
    }
  };

  // Warenkorb regelmäßig aktualisieren, wenn User angemeldet ist
  useEffect(() => {
    if (user?.token) {
      // Initial laden
      loadCart();
      
      // Periodische Aktualisierung alle 30 Sekunden
      const interval = setInterval(() => {
        loadCart();
      }, 30000);
      
      return () => clearInterval(interval);
    } else {
      // Warenkorb leeren wenn kein User
      setCart([]);
    }
  }, [user?.token]);

  // BANF-Anfragen und Entwürfe regelmäßig aktualisieren, wenn User angemeldet ist
  useEffect(() => {
    if (user?.token && ['purchaser', 'supervisor', 'admin'].includes(user?.role)) {
      // Initial laden
      loadPurchaseRequests();
      loadDrafts();

      // Periodische Aktualisierung alle 60 Sekunden (weniger häufig als Warenkorb)
      const interval = setInterval(() => {
        loadPurchaseRequests();
        loadDrafts();
      }, 60000);

      return () => clearInterval(interval);
    } else {
      // BANF-Anfragen und Entwürfe leeren wenn kein berechtigter User
      setPurchaseRequests([]);
      setDrafts([]);
    }
  }, [user?.token, user?.role]);

  // Zusätzliche Warenkorb-Aktualisierung beim Ansichtswechsel
  useEffect(() => {
    if (user?.token && (currentView === 'cart' || currentView === 'catalogs')) {
      loadCart();
    }
  }, [currentView, user?.token]);

  // Warenkorbmenge aktualisieren
  const updateCartQuantity = async (itemId, newQuantity) => {
    try {
      if (newQuantity <= 0) {
        await apiCall(`/cart/${itemId}`, 'DELETE', null, user);
      } else {
        await apiCall(`/cart/${itemId}`, 'PUT', { quantity: newQuantity }, user);
      }
      await loadCart(); // Warenkorb neu laden
    } catch (error) {
      console.error('Failed to update cart:', error);
    }
  };

  // Öffnet das Kontierungs-Popup; wirkt auf übergebene Positionen (Auswahl) oder sonst auf alle
  const applyKontierungToAll = (items) => {
    const targets = (Array.isArray(items) && items.length > 0) ? items : cart;
    if (!targets || targets.length === 0) return;
    const src = targets.find(i => i.cost_center || i.kostenstelle || i.gl_account || i.sachkonto) || targets[0];
    setKontierungTargets(targets);
    setKontierungPrefill({
      kontierungstyp: src.kontierungstyp || 'kostenstelle',
      auftrag: src.auftrag || '',
      cost_center: src.cost_center || src.kostenstelle || '',
      gl_account: src.gl_account || src.sachkonto || '',
      plant: src.plant || ''
    });
    setShowKontierungModal(true);
  };

  // Übernimmt die (im Popup angepasste) Kontierung auf die Ziel-Positionen (Auswahl oder alle)
  const confirmApplyKontierung = async (kont) => {
    const targets = (Array.isArray(kontierungTargets) && kontierungTargets.length > 0) ? kontierungTargets : cart;
    if (!targets || targets.length === 0 || !kont || Object.keys(kont).length === 0) {
      setShowKontierungModal(false);
      return;
    }
    try {
      for (const item of targets) {
        await apiCall(`/cart/items/${item.id}`, 'PUT', kont, user);
      }
      await loadCart();
      setShowKontierungModal(false);
      showSuccess(t('cart.kontierungAppliedAll'));
    } catch (error) {
      console.error('Kontierung fehlgeschlagen:', error);
      showError(t('cart.kontierungApplyError'));
    }
  };

  // Daten beim Start laden UND automatischen View-Wechsel nach Login
  useEffect(() => {
    if (user) {
      // User ist eingeloggt, wechsle zur passenden Ansicht
      if (currentView === 'login') {
        // Im OCI-Modus direkt zu Katalogen, sonst zur Startseite
        setViewHistory([]); // View-History beim Login leeren
        setCurrentView(ociMode ? 'catalogs' : 'home');
      }
      
      // Lade Daten je nach Benutzerrolle
      const loadData = async () => {
        try {
          console.log('🚀 Starting data load for user:', user.username);
          
          // Backend-Verbindung testen
          await testBackendConnection();
          
          // Shops parallel laden für bessere Performance
          console.log('📡 Loading shops in parallel...');
          const [ociShopsData, internalShopsData] = await Promise.all([
            loadOciShopsData(),
            loadInternalShopsData()
          ]);
          
          console.log('📋 Data load results:', {
            ociShops: ociShopsData?.length || 0,
            internalShops: internalShopsData?.length || 0,
            ociShopsData,
            internalShopsData
          });
          
          // State setzen
          console.log('💾 Setting state...');
          setOciShops(ociShopsData);
          setInternalShops(internalShopsData);
          
          // Kataloge mit beiden Shop-Arrays aktualisieren
          console.log('🔄 Updating catalogs...');
          updateCatalogs(ociShopsData, internalShopsData);
          
          if (user.role === 'admin') {
            await loadAdminUsers();
          }
          await loadSiteTitle();
          await loadCart();
          await loadPurchaseRequests(); // BANF-Anfragen für alle Rollen laden
          await loadSavedCarts(); // Gespeicherte Warenkörbe laden

          // Benutzerberechtigungen laden (für Modul-Gating in HomePage)
          try {
            const permsData = await apiCall('/auth/user/permissions', 'GET', null, user);
            if (permsData && permsData.permissions) {
              updateUser({ permissions: permsData.permissions });
            }
          } catch (permErr) {
            console.warn('Berechtigungen konnten nicht geladen werden:', permErr);
          }

          // Lizenz-Status laden (aeussere Schranke fuer Modul-Gating)
          try {
            const licData = await getLicense(user);
            if (licData && Array.isArray(licData.licensedModules)) {
              setLicenseState({
                licensedModules: new Set(licData.licensedModules),
                mode: licData.mode || 'grace',
              });
            } else {
              // Unerwartetes Format -> grace (alle lizenziert)
              setLicenseState({ licensedModules: new Set(), mode: 'grace' });
            }
          } catch (licErr) {
            console.warn('Lizenz-Status konnte nicht geladen werden (grace):', licErr);
            // Fallback: grace => alle Module bleiben nutzbar
            setLicenseState({ licensedModules: new Set(), mode: 'grace' });
          }

          console.log('✅ Data load completed');
        } catch (error) {
          console.error('❌ Error loading initial data:', error);
        }
      };
      
      loadData();
    }
    // Abhängigkeit bewusst auf den Token statt das ganze user-Objekt:
    // loadData() ruft updateUser({permissions}) auf, was ein neues user-Objekt
    // erzeugt. Mit [user] würde das den Effekt erneut auslösen → Endlosschleife
    // (Dauer-Reload + Flackern). Der Token ändert sich beim Permissions-Merge
    // nicht, daher läuft loadData genau einmal pro Login/Session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.token]);

  // Test-Funktion für Backend-Verbindung
  const testBackendConnection = async () => {
    console.log('🧪 Testing backend connection...');
    
    try {
      // Test Health-Check
      const healthResponse = await fetch('/api/health');
      if (healthResponse.ok) {
        const healthData = await healthResponse.json();
        console.log('✅ Backend health check passed:', healthData);
      } else {
        console.error('❌ Backend health check failed:', healthResponse.status);
      }
    } catch (error) {
      console.error('❌ Backend health check error:', error);
    }

    try {
      // Test OCI-Shops API (mit Token falls vorhanden)
      const ociHeaders = { 'Content-Type': 'application/json' };
      if (user?.token) {
        ociHeaders['Authorization'] = `Bearer ${user.token}`;
      }
      
      const ociResponse = await fetch('/api/oci-shops', { headers: ociHeaders });
      console.log('🧪 OCI-Shops API test:', {
        status: ociResponse.status,
        ok: ociResponse.ok,
        hasToken: !!user?.token
      });
      
      if (ociResponse.ok) {
        const ociData = await ociResponse.json();
        console.log('✅ OCI-Shops raw response:', ociData);
      } else {
        const errorText = await ociResponse.text();
        console.error('❌ OCI-Shops API error:', errorText);
      }
    } catch (error) {
      console.error('❌ OCI-Shops API connection error:', error);
    }

    try {
      // Test Internal-Shops API
      const internalHeaders = { 'Content-Type': 'application/json' };
      if (user?.token) {
        internalHeaders['Authorization'] = `Bearer ${user.token}`;
      }
      
      const internalResponse = await fetch('/api/internal-shops', { headers: internalHeaders });
      console.log('🧪 Internal-Shops API test:', {
        status: internalResponse.status,
        ok: internalResponse.ok,
        hasToken: !!user?.token
      });
      
      if (internalResponse.ok) {
        const internalData = await internalResponse.json();
        console.log('✅ Internal-Shops raw response:', internalData);
      } else {
        const errorText = await internalResponse.text();
        console.error('❌ Internal-Shops API error:', errorText);
      }
    } catch (error) {
      console.error('❌ Internal-Shops API connection error:', error);
    }
  };

  // API Calls - Separate Daten-Laden-Funktionen
  const loadOciShopsData = async () => {
    try {
      console.log('🔄 Loading OCI shops...');
      console.log('🔐 User token for OCI shops:', user?.token ? 'Present' : 'Missing');
      
      const shops = await apiCall('/oci-shops', 'GET', null, user);
      console.log('✅ OCI shops API response:', shops);
      console.log('📊 OCI shops type check:', typeof shops, Array.isArray(shops));
      
      if (!Array.isArray(shops)) {
        console.error('❌ Invalid OCI shops data received:', shops);
        return [];
      }
      
      console.log(`📦 Returning ${shops.length} OCI shops with details:`, shops.map(s => ({ 
        id: s.id, 
        name: s.name, 
        is_active: s.is_active, 
        hasCredentials: !!(s.username && s.password),
        base_url: s.base_url
      })));
      return shops;
    } catch (error) {
      console.error('❌ Failed to load OCI shops:', error);
      console.error('🚨 Error details:', { message: error.message, status: error.status, stack: error.stack });
      return [];
    }
  };

  const loadInternalShopsData = async () => {
    try {
      console.log('🔄 Loading internal shops...');
      console.log('🔍 User for internal shops request:', { username: user?.username, role: user?.role, hasToken: !!user?.token });
      console.log('🔐 User token for internal shops:', user?.token ? 'Present' : 'Missing');
      
      const shops = await apiCall('/internal-shops', 'GET', null, user);
      console.log('✅ Internal shops API response:', shops);
      console.log('📊 Internal shops type check:', typeof shops, Array.isArray(shops));
      
      if (!Array.isArray(shops)) {
        console.error('❌ Invalid internal shops data received:', shops);
        console.log('🔄 Fallback: Returning empty array for internal shops');
        return [];
      }
      
      console.log(`📦 Returning ${shops.length} internal shops with details:`, shops.map(s => ({ 
        id: s.id, 
        name: s.name, 
        is_active: s.is_active,
        category: s.category 
      })));
      return shops;
    } catch (error) {
      console.error('❌ Failed to load internal shops:', error);
      console.error('🚨 Error details:', { message: error.message, status: error.status, stack: error.stack });
      console.log('🔄 Fallback: API might not be available, returning empty array');
      
      // Wenn die API einen 500er Fehler zurückgibt (z.B. wegen fehlender Tabelle)
      if (error.status === 500 || error.message?.includes('Table') || error.message?.includes('doesn\'t exist')) {
        console.warn('⚠️ Internal shops table might not exist yet. This is normal for new installations.');
      }
      
      return [];
    }
  };

  // Wrapper-Funktionen für einzelne Updates (für Admin-Bereich)
  const loadOciShops = async () => {
    console.log('🔄 Reloading OCI shops only...');
    const shops = await loadOciShopsData();
    setOciShops(shops);
    // Warten auf State-Update, dann aktuelle internalShops verwenden
    setTimeout(() => {
      updateCatalogs(shops, internalShops);
    }, 100);
  };

  const loadInternalShops = async () => {
    console.log('🔄 Reloading internal shops only...');
    const shops = await loadInternalShopsData();
    setInternalShops(shops);
    // Warten auf State-Update, dann aktuelle ociShops verwenden
    setTimeout(() => {
      updateCatalogs(ociShops, shops);
    }, 100);
  };

  const updateCatalogs = (currentOciShops, currentInternalShops) => {
    try {
      console.log('🔄 Updating catalogs with data:', {
        ociShopsCount: currentOciShops?.length || 0,
        internalShopsCount: currentInternalShops?.length || 0,
        ociShopsData: currentOciShops,
        internalShopsData: currentInternalShops
      });

      // OCI-Shop Kataloge - weniger restriktive Filter
      const ociShops = currentOciShops || [];
      console.log('🔍 Filtering OCI shops:', ociShops);
      
      const activeOciShops = ociShops.filter(shop => {
        const isActive = shop.is_active === 1 || shop.is_active === true;
        const hasBaseUrl = shop.base_url && shop.base_url.trim() !== '';
        // Prüfe auf echte Credentials oder hasCredentials Flag oder maskierte Credentials
        const hasCredentials = shop.hasCredentials || 
                              (shop.username && shop.password) ||
                              (shop.username === '***masked***' && shop.password === '***masked***');
        
        console.log(`🔍 OCI Shop "${shop.name}":`, {
          isActive,
          hasBaseUrl,
          hasCredentials,
          credentialsInfo: shop.hasCredentials ? 'hasCredentials=true' : 
                          (shop.username === '***masked***' ? 'masked' : 'real'),
          willShow: isActive && hasBaseUrl && hasCredentials
        });
        
        return isActive && hasBaseUrl && hasCredentials;
      });
      
      const ociCatalogs = activeOciShops.map(shop => ({
        id: `oci-${shop.id}`,
        name: shop.name,
        supplier: shop.supplier || t('app.unknownSupplier'),
        url: shop.base_url,
        description: shop.description || 'Keine Beschreibung verfügbar',
        type: 'oci',
        shopId: shop.id,
        icon: shop.icon || '🏪'
      }));

      // Interne Shop Kataloge - weniger restriktive Filter
      const internalShops = currentInternalShops || [];
      console.log('🔍 Filtering internal shops:', internalShops);
      
      const activeInternalShops = internalShops.filter(shop => {
        const isActive = shop.is_active === 1 || shop.is_active === true;
        console.log(`🔍 Internal Shop "${shop.name}":`, {
          isActive,
          willShow: isActive
        });
        return isActive;
      });
      
      const internalCatalogs = activeInternalShops.map(shop => ({
        id: `internal-${shop.id}`,
        name: shop.name,
        supplier: t('app.internalShop'),
        url: null,
        description: shop.description || 'Keine Beschreibung verfügbar',
        type: 'internal',
        shopId: shop.id,
        icon: shop.icon || '🏪'
      }));

      const allCatalogs = [...ociCatalogs, ...internalCatalogs];
      console.log('✅ Generated catalogs:', {
        total: allCatalogs.length,
        oci: ociCatalogs.length,
        internal: internalCatalogs.length,
        catalogs: allCatalogs
      });
      
      setCatalogs(allCatalogs);
    } catch (error) {
      console.error('❌ Failed to update catalogs:', error);
      setCatalogs([]);
    }
  };

  // Admin-Benutzer laden
  const loadAdminUsers = async () => {
    try {
      const response = await apiCall('/admin/users', 'GET', null, user);
      if (response) {
        console.log('Loaded admin users:', response);
        setAdminUsers(response);
      }
    } catch (error) {
      console.error('Failed to load admin users:', error);
      setAdminUsers([]); // Leeres Array setzen bei Fehler
    }
  };

  // BANF-Anfragen laden
  const loadPurchaseRequests = async () => {
    try {
      if (!user || !['purchaser', 'supervisor', 'admin'].includes(user.role)) {
        setPurchaseRequests([]);
        return;
      }
      
      const response = await apiCall('/purchase-requests', 'GET', null, user);
      if (response && Array.isArray(response)) {
        console.log(`Loaded ${response.length} purchase requests for ${user.role} ${user.username}`);
        setPurchaseRequests(response);
      } else {
        console.warn('Invalid purchase requests response:', response);
        setPurchaseRequests([]);
      }
    } catch (error) {
      console.error('Failed to load purchase requests:', error);
      setPurchaseRequests([]);
    }
  };

  // Entwürfe laden (für Badge im Header)
  const loadDrafts = async () => {
    try {
      if (!user || !['purchaser', 'supervisor', 'admin'].includes(user.role)) {
        setDrafts([]);
        return;
      }

      const response = await apiCall('/purchase-requests/drafts', 'GET', null, user);
      if (response && Array.isArray(response)) {
        console.log(`Loaded ${response.length} drafts for ${user.username}`);
        setDrafts(response);
      } else {
        setDrafts([]);
      }
    } catch (error) {
      console.error('Failed to load drafts:', error);
      setDrafts([]);
    }
  };

  // Gespeicherte Warenkörbe laden
  const loadSavedCarts = async () => {
    if (!user?.token) return;
    try {
      const response = await getSavedCarts(user);
      if (Array.isArray(response)) {
        setSavedCarts(response);
      } else {
        setSavedCarts([]);
      }
    } catch (error) {
      console.error('Failed to load saved carts:', error);
      setSavedCarts([]);
    }
  };

  // Aktuellen Warenkorb als Vorlage speichern
  const saveCurrentCart = async (payload) => {
    try {
      // Auswahl speichern, falls vorhanden — sonst den ganzen Warenkorb
      const items = (Array.isArray(saveCartTargets) && saveCartTargets.length > 0) ? saveCartTargets : cart;
      const total = items.reduce((sum, i) => sum + (parseFloat(i.price) || 0) * (parseFloat(i.quantity) || 0), 0);
      const body = {
        name: payload.name,
        note: payload.note || '',
        tags: payload.tags || '',
        is_template: payload.is_template || 0,
        items_json: items,
        total_amount: total,
        currency: 'EUR',
      };
      await createSavedCart(user, body);
      await loadSavedCarts();
      showSuccess(t('savedCarts.saveSuccess'));
    } catch (error) {
      console.error('Failed to save cart:', error);
      showError(t('savedCarts.saveError'));
      throw error;
    }
  };

  // Gespeicherten Warenkorb in den aktiven Warenkorb laden
  const loadSavedCartToCart = async (savedCart, mode) => {
    try {
      const items = Array.isArray(savedCart.items) ? savedCart.items : [];
      if (items.length === 0) {
        showWarning(t('savedCarts.loadError'));
        return;
      }

      // Modus „Ersetzen": Warenkorb erst leeren
      if (mode === 'replace') {
        for (const item of cart) {
          await apiCall(`/cart/${item.id}`, 'DELETE', null, user);
        }
      }

      // Positionen einzeln einfügen — volle 1:1-Übernahme ALLER gespeicherten Felder.
      // apiCall wirft bei HTTP-Fehler → bricht die Schleife ab und landet im catch (kein stiller Teilzustand).
      for (const item of items) {
        // Schlüssel-/Audit-Felder der Quell-Zeile entfernen, der Rest wird 1:1 übernommen
        const { id, user_id, user_type, created_at, updated_at, position_number, ...cartItem } = item;
        await apiCall('/cart', 'POST', cartItem, user);
      }

      await loadCart();
      showSuccess(t('savedCarts.loadSuccess'));
      navigateTo('cart');
    } catch (error) {
      console.error('Failed to load saved cart:', error);
      await loadCart(); // tatsächlichen (ggf. Teil-)Zustand anzeigen
      showError(t('savedCarts.loadError'));
    }
  };

  // Gespeicherten Warenkorb umbenennen / Metadaten aktualisieren
  const renameSavedCart = async (id, fields) => {
    try {
      await updateSavedCart(user, id, fields);
      await loadSavedCarts();
      showSuccess(t('savedCarts.renameSuccess'));
    } catch (error) {
      console.error('Failed to rename saved cart:', error);
      showError(t('savedCarts.renameError'));
    }
  };

  // Gespeicherten Warenkorb löschen
  const handleDeleteSavedCart = async (id) => {
    try {
      await deleteSavedCart(user, id);
      await loadSavedCarts();
      showSuccess(t('savedCarts.deleteSuccess'));
    } catch (error) {
      console.error('Failed to delete saved cart:', error);
      showError(t('savedCarts.deleteError'));
    }
  };

  // OCI-URL generieren (mit konfigurierbaren Parametern)
  const generateOciUrl = async (shop, useIframe = false) => {
    if (!shop.username || !shop.password) {
      return null;
    }

    try {
      console.log('🔗 Generating OCI URL with configurable parameters for shop:', shop.id, 'iframe mode:', useIframe);
      
      // Ermittle die korrekte User-ID basierend auf OCI-Modus
      let effectiveUserId;
      if (ociMode && ociParams && ociParams.username) {
        // OCI-Benutzer: Verwende OCI-Username als Identifier
        effectiveUserId = ociParams.username;
        console.log(`🔑 OCI-Modus: Verwende OCI-Username als USER_ID: ${effectiveUserId}`);
      } else {
        // Admin-Benutzer: Verwende Admin-User-ID
        effectiveUserId = user.id;
        console.log(`🔑 Admin-Modus: Verwende Admin-User-ID: ${effectiveUserId}`);
      }

      // Dynamische URL-Generierung über Backend API
      const iframeParam = useIframe ? '&useIframe=true' : '';
      const response = await fetch(`/api/oci-shops/${shop.id}/generate-url?userId=${effectiveUserId}${iframeParam}`, {
        headers: {
          'Authorization': `Bearer ${user.token}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        console.log('✅ OCI URL generated with', result.parameterCount, 'parameters', useIframe ? '(iframe mode)' : '(popup mode)');
        return result.url;
      } else {
        console.warn('⚠️ Failed to generate dynamic OCI URL, falling back to legacy method');
        
        // Fallback zur alten Methode mit korrekter User-ID
        const baseUrl = shop.base_url.split('?')[0];
        const hookUrl = encodeURIComponent(`${window.location.origin}/api/oci/callback?HOOK_URL_USER_ID=${effectiveUserId}&HOOK_URL_SHOP_ID=${shop.id}`);
        const targetParam = useIframe ? '_self' : '_top';
        const url = `${baseUrl}?~OkCode=ADDI&~TARGET=${targetParam}&~CALLER=CTLG&~HTTP_CONTENT_CHARSET=UTF-8&USERNAME=${encodeURIComponent(shop.username)}&PASSWORD=${encodeURIComponent(shop.password)}&HOOK_URL=${hookUrl}`;
        
        return url;
      }
    } catch (error) {
      console.error('❌ Error generating OCI URL:', error);
      
      // Fallback zur alten Methode mit korrekter User-ID
      let effectiveUserId;
      if (ociMode && ociParams && ociParams.username) {
        effectiveUserId = ociParams.username;
      } else {
        effectiveUserId = user.id;
      }
      
      const baseUrl = shop.base_url.split('?')[0];
      const hookUrl = encodeURIComponent(`${window.location.origin}/api/oci/callback?HOOK_URL_USER_ID=${effectiveUserId}&HOOK_URL_SHOP_ID=${shop.id}`);
      const targetParam = useIframe ? '_self' : '_top';
      const url = `${baseUrl}?~OkCode=ADDI&~TARGET=${targetParam}&~CALLER=CTLG&~HTTP_CONTENT_CHARSET=UTF-8&USERNAME=${encodeURIComponent(shop.username)}&PASSWORD=${encodeURIComponent(shop.password)}&HOOK_URL=${hookUrl}`;
      
      return url;
    }
  };

  // Katalog auswählen
  const selectCatalog = async (catalog) => {
    // Handle Favorites catalog
    if (catalog.id === 'favoriten') {
      navigateTo('favorites');
      return;
    }

    if (catalog.type === 'oci') {
      // Echte Shop-Daten für OCI-Aufruf laden
      let realShopData;
      try {
        const realShops = await apiCall('/oci-shops/for-oci', 'GET', null, user);
        realShopData = realShops.find(s => s.id === catalog.shopId);
      } catch (error) {
        console.error('Failed to load real shop data:', error);
        showError(t('app.errorLoadingShopData'));
        return;
      }
      
      if (!realShopData || !realShopData.username || !realShopData.password) {
        showWarning('Dieser Shop ist noch nicht konfiguriert. Bitte wenden Sie sich an den Administrator.');
        return;
      }
      
      const ociUrl = await generateOciUrl(realShopData);
      if (!ociUrl) {
        showError('OCI-URL konnte nicht generiert werden.');
        return;
      }
      
      // Mobile Gerät: Direkt im Browser öffnen
      if (isMobile()) {
        console.log('Mobile device detected, opening directly in browser');
        window.location.href = ociUrl;
        return;
      }
      
      // Desktop: Maximiertes Pop-up-Fenster mit SAP-Kompatibilität
      let features;
      
      // Spezielle Behandlung für OCI-eingeloggte Benutzer
      if (ociMode && ociParams) {
        // OCI-Benutzer: Immer maximiertes Pop-up verwenden (SAP SimpleBrowse)
        console.log('🔗 OCI-Benutzer erkannt - verwende SAP SimpleBrowse (Pop-up)');
        
        const popupOciUrl = await generateOciUrl(realShopData, false); // useIframe = false
        if (!popupOciUrl) {
          showError('OCI-URL konnte nicht generiert werden.');
          return;
        }

        // Maximiertes Pop-up-Fenster für SAP SimpleBrowse
        const features = [
          'width=' + window.screen.availWidth,
          'height=' + window.screen.availHeight,
          'left=0',
          'top=0',
          'scrollbars=yes',
          'resizable=yes'
        ].join(',');

        const ociWindow = window.open(popupOciUrl, '_blank', features);
        
        if (!ociWindow) {
          showError('Pop-up wurde blockiert. Bitte erlauben Sie Pop-ups für diese Website.');
          return;
        }

        // Pop-up überwachen
        const checkClosed = setInterval(() => {
          if (ociWindow.closed) {
            clearInterval(checkClosed);
            console.log('OCI-Session beendet');
            loadCart();
          }
        }, 1000);

        return;
      } else {
        // Standard Desktop-Parameter
        features = [
          'width=' + window.screen.availWidth,
          'height=' + window.screen.availHeight,
          'left=0',
          'top=0',
          'scrollbars=yes',
          'resizable=yes'
        ].join(',');
      }
      
      const ociWindow = window.open(ociUrl, '_blank', features);
      
      if (!ociWindow) {
        // Fallback für blockierte Popups: In-App-Browser verwenden
        console.log('🚫 Pop-up blockiert - verwende In-App-Browser als Fallback');
        const iframeOciUrl = await generateOciUrl(realShopData, true); // useIframe = true
        if (!iframeOciUrl) {
          showError('OCI-URL konnte nicht für iframe-Modus generiert werden.');
          return;
        }
        setOciFrameUrl(iframeOciUrl);
        setShowOciFrame(true);
        return;
      }
      
      const checkClosed = setInterval(() => {
        if (ociWindow.closed) {
          clearInterval(checkClosed);
          console.log('OCI-Session beendet');
          // Warenkorb neu laden für den Fall, dass Artikel hinzugefügt wurden
          loadCart();
        }
      }, 1000);
      
    } else if (catalog.type === 'internal') {
      // Interne Shop-Produkte laden
      setSelectedCatalog(catalog);
      setLoading(true);
      try {
        const response = await apiCall(`/internal-shops/${catalog.shopId}/products`, 'GET', null, user);
        
        // Produktstruktur für ProductView anpassen
        const mappedProducts = (response || []).map(product => ({
          id: product.id,
          created_at: product.created_at || null,
          name: product.name,
          description: product.description,
          price: product.price,
          currency: product.currency || 'EUR',
          unit: product.unit || 'STK',
          category: product.category,
          category_label: product.category_label || null,
          sku: product.sku,
          vendor: product.vendor || '',
          vendormat: product.vendor_mat || product.vendormat || '',
          matnr: product.sku || '',
          vendor_mat: product.vendor_mat || '',
          preferred_vendor: product.preferred_vendor || '',
          fixed_vendor: product.fixed_vendor || '',
          image_url: product.image_url,
          stock_quantity: product.stock_quantity,
          min_order_quantity: product.min_order_quantity,
          shop_id: catalog.shopId,
          // SAP-Felder für Pflichtfeldprüfung
          cost_center: product.cost_center || '',
          purchasing_org: product.purchasing_org || '',
          plant: product.plant || '',
          // Reiche Detail-Felder (Produktdetail-Seite)
          price_source: product.price_source || '',
          manufacturer: product.manufacturer || '',
          manufacturer_part_no: product.manufacturer_part_no || '',
          purchasing_group: product.purchasing_group || '',
          planned_delivery_time: product.planned_delivery_time || '',
          technical_attributes: product.technical_attributes || null,
          price_scales: product.price_scales || null,
          contract_data: product.contract_data || null,
          documents: product.documents || null,
          compliance: product.compliance || null,
          // P1: SAP-Material-Skalare + Kind-Arrays (Mehrsprachig MAKT + Multi-Lagerort MARD)
          source: product.source || 'internal',
          ean: product.ean || '',
          price_unit: product.price_unit || 1,
          material_type: product.material_type || '',
          status: product.status || 'active',
          gross_weight: product.gross_weight ?? null,
          net_weight: product.net_weight ?? null,
          weight_unit: product.weight_unit || '',
          size_text: product.size_text || '',
          profit_center: product.profit_center || '',
          texts: Array.isArray(product.texts) ? product.texts : [],
          stock: Array.isArray(product.stock) ? product.stock : [],
          sources: Array.isArray(product.sources) ? product.sources : [],
          order_history: Array.isArray(product.order_history) ? product.order_history : [],
          attributes: Array.isArray(product.attributes) ? product.attributes : []
        }));
        
        console.log('Mapped internal shop products:', mappedProducts);
        setProducts(mappedProducts);
        navigateTo('products');
      } catch (error) {
        console.error('Failed to load internal shop products:', error);
        showError(t('app.shopLoadError'));
        setProducts([]);
      } finally {
        setLoading(false);
      }
    }
  };

  // OCI-Frame schließen
  const closeOciFrame = () => {
    setShowOciFrame(false);
    setOciFrameUrl(null);
    // Warenkorb neu laden falls Artikel hinzugefügt wurden
    loadCart();
  };



  // Warenkorb-Funktionen
  const addToCart = async (product, additionalFields = null, quantity = 1) => {
    try {
      // Für interne Shops shop_id als negativen Wert oder null senden
      const shopId = selectedCatalog?.type === 'internal' ? null : selectedCatalog?.shopId;
      const isInternalCatalog = selectedCatalog?.type === 'internal';

      console.log('🛒 addToCart called:', {
        productName: product?.name,
        isInternalCatalog,
        selectedCatalogType: selectedCatalog?.type,
        additionalFields,
        productFields: {
          cost_center: product?.cost_center,
          purchasing_org: product?.purchasing_org,
          plant: product?.plant
        }
      });

      // Bei internen Katalogen: Prüfen ob Pflichtfelder im PRODUKT vorhanden sind
      if (isInternalCatalog && !additionalFields) {
        const missing = [];

        // Prüfe nur die Produkt-Felder (nicht User-Defaults)
        // Kostenstelle
        if (!product.cost_center) {
          missing.push('cost_center');
        }

        // Einkaufsorganisation
        if (!product.purchasing_org) {
          missing.push('purchasing_org');
        }

        // Werk
        if (!product.plant) {
          missing.push('plant');
        }

        console.log('🔍 Prüfung fehlender Felder:', { missing, total: missing.length });

        // Wenn Felder fehlen, Modal anzeigen
        if (missing.length > 0) {
          console.log('⚠️ Fehlende Pflichtfelder für internes Produkt:', missing, product);
          setPendingCartProduct(product);
          setMissingFields(missing);
          setShowMissingFieldsModal(true);
          return;
        }
      }

      // Primäre Bezugsquelle aus sources[]
      const primarySrc = Array.isArray(product.sources)
        ? (product.sources.find(s => s.is_primary) || product.sources[0])
        : null;

      // Felder zusammenführen. Kontierungsfelder (cost_center/gl_account/auftrag):
      // wurde das Modal bestätigt (additionalFields gesetzt), gilt DESSEN Wert verbindlich —
      // auch leer. Das Modal blendet typfremde Felder aus und sendet sie bewusst als '';
      // eine ||-Kette würde die geleerten Werte sonst mit Produkt-/User-Defaults auffüllen
      // (z. B. Kostenstelle bei Kontierungstyp 'material').
      const finalCostCenter = additionalFields ? (additionalFields.cost_center || '') : (product.cost_center || user?.costCenter || '');
      const finalPurchasingOrg = additionalFields?.purchasing_org || product.purchasing_org || user?.purchasingOrg || '';
      const finalPlant = additionalFields?.plant || product.plant || user?.plant || '';
      const finalEinkauefergruppe = additionalFields?.einkauefergruppe || product.purchasing_group || '';
      const finalPreferredVendor = additionalFields?.preferred_vendor || product.preferred_vendor || primarySrc?.vendor_name || '';
      const finalVendorMaterialNumber = product.vendor_mat || product.vendormat || primarySrc?.vendor_material_no || '';
      const finalInfoRecord = additionalFields?.info_record || primarySrc?.info_record_no || '';
      const finalGlAccount = additionalFields?.gl_account || '';
      const finalKontierungstyp = additionalFields?.kontierungstyp || 'kostenstelle';
      const finalAuftrag = additionalFields ? (additionalFields.auftrag || '') : (product.auftrag || '');

      const cartItem = {
        description: product.name || product.description || t('app.unnamed'),
        quantity: sanitizeQuantity(quantity, product.unit || 'ST') || 1,
        price: parseFloat(product.price) || 0,
        // PEINH mitgeben — SAP-Preise gelten oft pro 100/1000 (z.B. 150,78 EUR pro 1000 ST);
        // ohne price_unit setzt das Backend 1 und der Bestellwert ist um den Faktor falsch
        price_unit: parseInt(product.price_unit, 10) || 1,
        currency: product.currency || 'EUR',
        unit: product.unit || 'ST',
        image_url: product.image_url || '',
        vendormat: product.vendormat || product.vendor_mat || '',
        vendor: isInternalCatalog ? '' : (product.vendor || ''),
        shop_id: shopId,
        // Produkt-Stammdaten in die Position übernehmen: Warengruppe, SAP-/Materialnr., Lieferanten-Artikelnr.
        material: product.sku || product.matnr || '',
        product_group: product.category || '',
        material_group: product.category || '',
        vendor_material_number: finalVendorMaterialNumber,
        preferred_vendor: finalPreferredVendor,
        fixed_vendor: product.fixed_vendor || '',
        // Carry-over aus primärer Bezugsquelle / additionalFields
        einkauefergruppe: finalEinkauefergruppe || undefined,
        info_record: finalInfoRecord || undefined,
        gl_account: finalGlAccount || undefined,
        sachkonto: finalGlAccount || undefined,
        kontierungstyp: finalKontierungstyp,
        auftrag: finalAuftrag || undefined,
        // SAP-Felder für interne Kataloge (Backend erwartet einkaueferorganisation statt purchasing_org)
        cost_center: isInternalCatalog ? finalCostCenter : undefined,
        einkaueferorganisation: isInternalCatalog ? finalPurchasingOrg : undefined,
        plant: isInternalCatalog ? finalPlant : undefined
      };

      const response = await fetch('/api/cart', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify(cartItem)
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Artikel zum Warenkorb hinzugefügt:', result);

        // Warenkorb neu laden
        await loadCart();

        // Kurze Erfolgsmeldung
        showSuccess(t('app.addedToCart', { name: cartItem.description }));
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || t('app.errorAddingToCart'));
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      showError(t('app.errorAddingToCart') + ': ' + error.message);
    }
  };

  // Handler für MissingFieldsModal-Bestätigung
  const handleMissingFieldsConfirm = async (fields) => {
    setShowMissingFieldsModal(false);
    if (pendingCartProduct) {
      await addToCart(pendingCartProduct, fields);
      setPendingCartProduct(null);
      setMissingFields([]);
    }
  };

  // Handler für MissingFieldsModal-Abbruch
  const handleMissingFieldsClose = () => {
    setShowMissingFieldsModal(false);
    setPendingCartProduct(null);
    setMissingFields([]);
  };

  // Gesamtsumme des Warenkorbs berechnen
  const getCartTotal = () => {
    return cart.reduce((total, item) => {
      return total + (item.price * item.quantity);
    }, 0).toFixed(2);
  };

  // BANF-Anfrage erstellen (neuer Genehmigungsworkflow)
  const createPurchaseRequisition = async (selectedItemsArray = null) => {
    try {
      const itemsToProcess = selectedItemsArray || cart;
      if (itemsToProcess.length === 0) {
        showWarning(selectedItemsArray ? 'Keine Artikel ausgewählt.' : 'Ihr Warenkorb ist leer. Fügen Sie erst Artikel hinzu.');
        return;
      }

      // Wenn nur ausgewählte Artikel verarbeitet werden, sende ihre IDs
      const requestBody = selectedItemsArray ? { selectedItemIds: selectedItemsArray.map(item => item.id) } : null;
      const response = await apiCall('/cart/purchase', 'POST', requestBody, user);
      
      // Warenkorb neu laden (sollte jetzt leer sein)
      await loadCart();
      await loadPurchaseRequests(); // BANF-Anfragen neu laden für Badge-Update
      
      const articleText = selectedItemsArray ? `${selectedItemsArray.length} ausgewählte Artikel` : `${response.itemCount} Artikel`;
      showSuccess(`${response.message} (${articleText}, Gesamt: ${response.totalAmount} ${response.currency})`);
    } catch (error) {
      console.error('Failed to create purchase request:', error);
      showError(t('app.errorCreatingBanf') + ': ' + error.message);
    }
  };
  
  // OCI-Checkout für SAP-Übertragung
  const ociCheckout = async (selectedItemsArray = null) => {
    if (!ociMode || !ociParams) {
      showError('OCI-Mode nicht aktiv');
      return;
    }
    
    const itemsToProcess = selectedItemsArray || cart;
    if (itemsToProcess.length === 0) {
      showWarning(selectedItemsArray ? 'Keine Artikel ausgewählt' : 'Warenkorb ist leer');
      return;
    }
    
    // Sofortige Prüfung auf SAP-Integration
    const isSapIntegration = ociParams?.hookUrl && (
      ociParams.hookUrl.includes('sap-cust://') || 
      ociParams.hookUrl.includes('SAPEVENT:') ||
      ociParams.hookUrl.includes('sap-place-holder')
    );
    
    if (isSapIntegration) {
      console.log('⚡ SAP-Integration erkannt - bereite sofortiges Schließen vor');
    }
    
    try {
      setLoading(true);
      console.log('🛒 Starting OCI checkout...');
      
             const response = await fetch('/api/oci/checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId: ociParams.sessionId,
          userId: user.id,
          hookUrl: ociParams.hookUrl,
          target: ociParams.target,
          selectedItemIds: selectedItemsArray ? selectedItemsArray.map(item => item.id) : null
        })
      });
      
            if (response.ok) {
        const htmlContent = await response.text();
        console.log('📤 OCI checkout successful, creating transfer page...');
        
        // Prüfe ob SAP-Integration - dann sofortiges Schließen
        const isSapIntegration = ociMode && ociParams?.hookUrl && (
          ociParams.hookUrl.includes('sap-cust://') || 
          ociParams.hookUrl.includes('SAPEVENT:') ||
          ociParams.hookUrl.includes('sap-place-holder')
        );
        
        if (isSapIntegration) {
          // Für SAP: Sofortige Form-Submission zur Hook-URL im gleichen Fenster
          console.log('🚀 SAP-Integration: Sofortige OCI-Form-Submission');
          
          const articleCount = selectedItemsArray ? selectedItemsArray.length : cart.length;
          const articleText = selectedItemsArray ? `${articleCount} ausgewählte` : `${articleCount}`;
          showSuccess(`✅ ${articleText} Artikel werden SOFORT an SAP übertragen!`);
          
          // Warenkorb sofort leeren
          setCart([]);
          
          // Sofortige OCI-Form-Submission zur Hook-URL
          console.log('🎯 Erstelle OCI-Form für direkte SAP-Übertragung');
          
          // Parsen der OCI-Daten aus der HTML-Response
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = htmlContent;
          const hiddenInputs = tempDiv.querySelectorAll('input[type="hidden"]');
          
          // Erstelle und submitte OCI-Form im aktuellen Fenster
          const ociForm = document.createElement('form');
          ociForm.method = 'POST';
          ociForm.action = ociParams.hookUrl;
          ociForm.target = '_self'; // Im gleichen Fenster - kehrt zu SAP zurück
          ociForm.style.display = 'none';
          
          // Kopiere alle OCI-Daten
          hiddenInputs.forEach(input => {
            const newInput = document.createElement('input');
            newInput.type = 'hidden';
            newInput.name = input.name;
            newInput.value = input.value;
            ociForm.appendChild(newInput);
          });
          
          // Füge SAP-spezifische Parameter hinzu
          const sapParams = [
            { name: '~OkCode', value: 'ADDI' },
            { name: '~TARGET', value: '_self' },
            { name: '~CALLER', value: 'ProCureX' },
            { name: '~CLOSE_CATALOG', value: 'TRUE' },
            { name: '~RETURN_URL', value: 'BACK' }
          ];
          
          sapParams.forEach(param => {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = param.name;
            input.value = param.value;
            ociForm.appendChild(input);
          });
          
          // Form zur Seite hinzufügen und SOFORT submitten
          document.body.appendChild(ociForm);
          
          console.log('📤 Submitte OCI-Form SOFORT zur SAP-Hook-URL:', ociParams.hookUrl);
          try {
            ociForm.submit();
            console.log('✅ OCI-Form erfolgreich an SAP gesendet - Sofortige Übertragung!');
          } catch (error) {
            console.log('⚠️ Form-Submit fehlgeschlagen:', error);
            // Fallback: Direkte Navigation
            window.location.href = ociParams.hookUrl;
          }
          
        } else {
          // Standard-Verhalten für normale URLs
          const transferWindow = window.open('', '_blank', 'width=600,height=400');
          transferWindow.document.write(htmlContent);
          transferWindow.document.close();
          
          const articleCount = selectedItemsArray ? selectedItemsArray.length : cart.length;
          const articleText = selectedItemsArray ? `${articleCount} ausgewählte` : `${articleCount}`;
          showSuccess(`✅ ${articleText} Artikel werden an SAP übertragen...`);
          
          // Warenkorb leeren nach erfolgreicher Übertragung
          setCart([]);
                }
        
      } else {
        const error = await response.json();
        throw new Error(error.error || t('app.unknownError'));
      }
    } catch (error) {
      console.error('Error in OCI checkout:', error);
      showError('❌ Fehler bei der SAP-Übertragung: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Admin-Funktionen
  const saveShop = async (shopData) => {
    try {
      const method = shopData.id ? 'PUT' : 'POST';
      const endpoint = shopData.id ? `/oci-shops/${shopData.id}` : '/oci-shops';
      
      await apiCall(endpoint, method, shopData, user);
      await loadOciShops();
      setEditingShop(null);
      showSuccess(t('app.shopSaved'));
    } catch (error) {
      console.error('Failed to save shop:', error);
      showError(t('app.errorSavingShop') + ': ' + error.message);
    }
  };

  const deleteShop = async (shopId) => {
    if (!window.confirm('Sind Sie sicher, dass Sie diesen Shop löschen möchten?')) {
      return;
    }
    
    try {
      await apiCall(`/oci-shops/${shopId}`, 'DELETE', null, user);
      await loadOciShops();
      showSuccess('Shop erfolgreich gelöscht!');
    } catch (error) {
      console.error('Failed to delete shop:', error);
      showError(t('app.errorDeletingShop'));
    }
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // User-State aktualisieren
  const handleUserUpdate = (updatedUser) => {
    // Wenn der Seitentitel in den Einstellungen geändert wurde, aktualisieren wir ihn auch im User-Objekt
    if (updatedUser.siteTitle) {
      document.title = updatedUser.siteTitle;
    }
    updateUser(updatedUser);
  };

  // Schließ-Funktion für SAP-Integration (global verfügbar)
  window.closeSAPWindow = () => {
    console.log('🔥 Manual SAP Window Close triggered');
    
    try {
      if (window.opener) {
        console.log('🎯 Closing via opener');
        window.opener.close();
      }
      if (window.parent && window.parent !== window) {
        console.log('🎯 Closing via parent');
        window.parent.close();
      }
      if (window.top && window.top !== window) {
        console.log('🎯 Closing via top');
        window.top.close();
      }
      console.log('🎯 Closing current window');
      window.close();
    } catch (error) {
      console.log('⚠️ Close failed, navigating to blank:', error);
      window.location.href = 'about:blank';
    }
  };

  // Render Logic
  const renderCurrentView = () => {
    // Debug nur bei tatsächlichen Änderungen
    if (window.debugRender) {
      console.log('🎯 renderCurrentView called with currentView:', currentView);
    }

    // --- Modul-Register: einfache Module ohne View-spezifische Props ---
    // Wird VOR dem Switch geprueft. Nur Module mit simplen Props (user, onBack,
    // showSuccess, showError) landen hier. Module mit eigenen komplexen Props
    // (catalogs, cart, lager onNavigateToCreateBanf/onCartUpdated, ...) bleiben
    // im bestehenden Switch-Fallback unberuehrt.
    //
    // Aktuell via Register geroutet:  einvoicing, plant-maintenance,
    //                                 material-schnellerfassung, auftragsbestaetigung.
    // Bewusst im Switch belassen:     catalogs (ociMode, savedCarts, ...),
    //                                 lager (onCartUpdated, onNavigateToCreateBanf),
    //                                 cart, purchase-requests, create-banf, ...
    const _registrySimpleIds = new Set([
      'einvoicing',
      'plant-maintenance',
      'material-schnellerfassung',
      'auftragsbestaetigung',
    ]);
    if (_registrySimpleIds.has(currentView)) {
      const mod = getModule(currentView);
      if (mod && hasModuleAccess(mod, user, licenseState)) {
        const ModView = mod.View;
        return (
          <ModView
            user={user}
            onBack={goBack}
            showSuccess={showSuccess}
            showError={showError}
          />
        );
      }
      // Kein Zugriff oder Modul nicht gefunden: Home rendern (kein State-Update im Render)
      return (
        <HomePage
          onNavigate={navigateTo}
          user={user}
          licenseState={licenseState}
        />
      );
    }

    switch (currentView) {
      case 'login':
        return (
          <LoginView
            onLogin={handleLogin}
            logo={globalLogo}
            siteTitle={siteTitle}
          />
        );

      case 'home':
        return (
          <HomePage
            onNavigate={navigateTo}
            user={user}
            licenseState={licenseState}
          />
        );

      case 'catalogs':
        return (
          <CatalogView
            user={user}
            catalogs={catalogs}
            onSelectCatalog={selectCatalog}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            ociMode={ociMode}
            ociParams={ociParams}
            onBack={goBack}
            cart={cart}
            getCartTotal={getCartTotal}
            drafts={drafts}
            purchaseRequests={purchaseRequests}
            onOpenCart={() => navigateTo('cart')}
            onNewBanf={() => { setEditingDraft(null); navigateTo('create-banf'); }}
            onOpenDraft={(draft) => { setEditingDraft(draft); navigateTo('create-banf'); }}
            onOpenRequests={openBanfList}
            savedCarts={savedCarts}
            onLoadSavedCart={(sc, mode) => {
              loadSavedCartToCart(sc, mode);
            }}
            onRenameSavedCart={renameSavedCart}
            onDeleteSavedCart={handleDeleteSavedCart}
          />
        );
      
      case 'products':
        return (
          <ProductView
            selectedCatalog={selectedCatalog}
            products={products}
            searchTerm={searchTerm}
            loading={loading}
            onAddToCart={addToCart}
            onSelectProduct={(p) => { setSelectedProduct(p); navigateTo('product-detail'); }}
            onBack={goBack}
            onSearchChange={setSearchTerm}
            user={user}
          />
        );

      case 'product-detail':
        return (
          <ProductDetailView
            product={selectedProduct}
            onAddToCart={addToCart}
            onBack={goBack}
            user={user}
          />
        );

      case 'cart':
        console.log('🛒 Rendering CartView with:', {
          cartLength: cart?.length,
          ociMode,
          hasOciParams: !!ociParams,
          user: user?.username
        });
        return (
          <>
          <CartView
            cart={cart}
            updateCartQuantity={updateCartQuantity}
            getCartTotal={getCartTotal}
            onBack={goBack}
            createPurchaseRequisition={createPurchaseRequisition}
            ociCheckout={ociCheckout}
            ociMode={ociMode}
            ociParams={ociParams}
            user={user}
            onEditItem={(item) => {
              // Store item data for editing and navigate to CreateBanfView
              setEditingCartItem(item);
              navigateTo('create-banf');
            }}
            onApplyKontierungToAll={applyKontierungToAll}
            showSuccess={showSuccess}
            showError={showError}
            savedCarts={savedCarts}
            onOpenSaveCartModal={(items) => { setSaveCartTargets(Array.isArray(items) ? items : []); setShowSaveCartModal(true); }}
            onLoadSavedCart={loadSavedCartToCart}
            onRenameSavedCart={renameSavedCart}
            onDeleteSavedCart={handleDeleteSavedCart}
          />
          <KontierungBulkModal
            isOpen={showKontierungModal}
            onClose={() => setShowKontierungModal(false)}
            onApply={confirmApplyKontierung}
            initial={kontierungPrefill}
            itemCount={kontierungTargets.length > 0 ? kontierungTargets.length : (cart?.length || 0)}
            user={user}
          />
          <SaveCartModal
            isOpen={showSaveCartModal}
            onClose={() => setShowSaveCartModal(false)}
            onSave={saveCurrentCart}
            itemCount={saveCartTargets.length > 0 ? saveCartTargets.length : (cart?.length || 0)}
            totalAmount={(saveCartTargets.length > 0 ? saveCartTargets : (cart || [])).reduce((s, i) => s + (parseFloat(i.price) || 0) * (parseFloat(i.quantity) || 0), 0)}
          />
          </>
        );

      case 'settings':
        return (
          <SettingsView
            user={user}
            onBack={goBack}
            onUserUpdated={handleUserUpdate}
            showSuccess={showSuccess}
            showError={showError}
          />
        );

      case 'favorites':
        return (
          <FavoritesView
            user={user}
            onBack={goBack}
            addToCart={addToCart}
            showSuccess={showSuccess}
            showError={showError}
            onEditFavorite={(favorite) => {
              setEditingFavorite(favorite);
              navigateTo('create-banf');
            }}
          />
        );

      case 'admin':
        if (editingShop) {
          return (
            <ShopEditor
              shop={editingShop}
              onSave={saveShop}
              onClose={() => setEditingShop(null)}
              user={user}
            />
          );
        }
        return (
          <AdminView
            user={user}
            adminUsers={adminUsers}
            ociShops={ociShops}
            onEditShop={(shop) => {
              setAdminActiveTab('shops'); // Merken, dass wir einen Shop bearbeiten
              setEditingShop(shop);
            }}
            onDeleteShop={deleteShop}
            onReloadUsers={loadAdminUsers}
            onUpdateUser={handleUserUpdate}
            onBack={goBack}
            activeTab={adminActiveTab}
            onTabChange={setAdminActiveTab}
            showSuccess={showSuccess}
            showError={showError}
            showWarning={showWarning}
            showInfo={showInfo}
          />
        );

      case 'purchase-requests':
        return (
          <BanfListView
            user={user}
            initialTab={banfListNav.tab}
            navNonce={banfListNav.n}
            onBack={goBack}
            onRequestUpdate={loadPurchaseRequests}
            onDraftsChange={loadDrafts}
            onEditDraft={(draft) => {
              setEditingDraft(draft);
              navigateTo('create-banf');
            }}
          />
        );

      case 'create-banf':
        return (
          <CreateBanfView
            user={user}
            onBack={() => {
              // Spezial-States bereinigen, dann Stack-basiert zurueck
              if (editingCartItem) setEditingCartItem(null);
              if (editingFavorite) setEditingFavorite(null);
              if (editingDraft) setEditingDraft(null);
              if (ociItems) setOciItems(null);
              goBack();
            }}
            onBanfCreated={loadPurchaseRequests}
            onGoToCart={() => setCurrentView('cart')}
            addToCart={addToCart}
            showSuccess={showSuccess}
            showError={showError}
            editingCartItem={editingCartItem}
            editingFavorite={editingFavorite}
            editingDraft={editingDraft}
            ociItems={ociItems}
            onCartItemUpdated={() => {
              setEditingCartItem(null);
              setCurrentView('cart');
              loadCart(); // Use loadCart instead of reloadCart
            }}
            onFavoriteUpdated={() => {
              setEditingFavorite(null);
              setCurrentView('favorites');
            }}
            onDraftUpdated={() => {
              setEditingDraft(null);
              setCurrentView('purchase-requests');
              loadDrafts();
            }}
            onDraftsChange={loadDrafts}
            onOciItemsProcessed={() => {
              setOciItems(null);
              setCurrentView('cart');
              loadCart();
            }}
          />
        );
      
      case 'lager':
        return (
          <LagerShell
            user={user}
            onBack={() => navigateTo('home')}
            showSuccess={showSuccess}
            showError={showError}
            onCartUpdated={loadCart}
            onNavigateToCreateBanf={(prefill) => {
              // Lager-Schnell-BANF: Vorbefuellung als OCI-Item-Array uebergeben
              // (nutzt denselben ociItems-Mechanismus wie OCI-Punch-out in CreateBanfView)
              setOciItems([{
                description: prefill.description || '',
                quantity: prefill.quantity || 1,
                unit: prefill.unit || 'ST',
                price: prefill.price || '',
                currency: prefill.currency || 'EUR',
                vendor: prefill.preferred_vendor || '',
                preferred_vendor: prefill.preferred_vendor || '',
                material: prefill.material || '',
                material_group: prefill.material_group || '',
                plant: prefill.plant || '',
                delivery_date: prefill.delivery_date || '',
                herkunft: prefill.herkunft || 'LAGER_MODUL',
              }]);
              navigateTo('create-banf');
            }}
          />
        );

      default:
        console.log('❌ Unknown view:', currentView);
        return <div>404 - View not found: {currentView}</div>;
    }
  };

  // Separate useEffect für currentView changes (nur zur Aktualisierung nach Admin-Änderungen)
  useEffect(() => {
    // Nur laden wenn wir vom Admin-Bereich zurückkommen und bereits Daten haben
    if (user && currentView === 'catalogs' && (ociShops.length > 0 || internalShops.length > 0)) {
      console.log('🔄 Refreshing catalogs after view change...');
      // Beide Shop-Typen neu laden und Kataloge aktualisieren
      const refreshCatalogs = async () => {
        const [newOciShops, newInternalShops] = await Promise.all([
          loadOciShopsData(),
          loadInternalShopsData()
        ]);
        setOciShops(newOciShops);
        setInternalShops(newInternalShops);
        updateCatalogs(newOciShops, newInternalShops);
      };
      refreshCatalogs();
    }
  }, [currentView]);

  return (
    <>
      <div className={`min-h-screen ${isDarkMode 
        ? 'bg-gradient-to-br from-slate-900 via-gray-900 to-slate-800' 
        : 'bg-gradient-to-br from-blue-50 via-white to-gray-50'
      } flex flex-col`}>
      {isLoading ? (
        // Loading Screen während Session-Check
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-2xl p-8 w-full max-w-md text-center">
            <div className="bg-blue-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <Package className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-800 mb-4">SAP OCI Portal</h1>
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
            <p className="text-gray-600 mt-3">{t('login.checkingSession')}</p>
          </div>
        </div>
      ) : currentView === 'login' ? (
        <div className="min-h-screen flex flex-col">
          <div className={`flex-1 flex items-center justify-center ${isDarkMode 
            ? 'bg-gradient-to-br from-slate-900 via-gray-900 to-slate-800' 
            : 'bg-gradient-to-br from-blue-50 via-white to-gray-50'
          } p-4 relative overflow-hidden`}>
            {/* Liquid Glass Background Elements */}
            <div className={`absolute inset-0 ${isDarkMode 
              ? 'bg-gradient-to-br from-blue-900/20 via-transparent to-purple-900/20' 
              : 'bg-gradient-to-br from-blue-100/30 via-transparent to-indigo-100/30'
            }`}></div>
            <div className={`absolute top-1/4 left-1/4 w-96 h-96 ${isDarkMode 
              ? 'bg-blue-500/10' 
              : 'bg-blue-200/20'
            } rounded-full blur-3xl`}></div>
            <div className={`absolute bottom-1/4 right-1/4 w-80 h-80 ${isDarkMode 
              ? 'bg-purple-500/10' 
              : 'bg-indigo-200/20'
            } rounded-full blur-3xl`}></div>
            <div className={`absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] ${isDarkMode 
              ? 'bg-gradient-radial from-white/5 to-transparent' 
              : 'bg-gradient-radial from-blue-100/10 to-transparent'
            } rounded-full blur-2xl`}></div>
            
            {/* Content */}
            <div className="relative z-10">
              {renderCurrentView()}
            </div>
          </div>
          <Footer />
        </div>
      ) : (
        <>
          <Header
            user={user}
            cart={cart}
            purchaseRequests={purchaseRequests}
            drafts={drafts}
            currentView={currentView}
            onLogout={handleLogoutRequest}
            onNavigate={(view) => {
              console.log('🔄 Navigation to:', view, 'from:', currentView);
              if (view === 'cart') {
                console.log('🛒 Navigating to cart, current cart:', cart.length, 'items');
                loadCart(); // Warenkorb vor Navigation neu laden
              }
              if (view === 'purchase-requests') {
                loadDrafts(); // Entwürfe neu laden beim Navigieren
              }
              navigateTo(view);
            }}
            onOpenRequests={openBanfList}
            siteTitle={siteTitle}
            logo={globalLogo}
            ociMode={ociMode}
          />
          <div className="flex-1">
            {renderCurrentView()}
          </div>
          <Footer />
          {editingShop && (
            <ShopEditor
              shop={editingShop}
              onSave={saveShop}
              onClose={() => setEditingShop(null)}
              user={user}
            />
          )}
          
          {/* Logout Confirmation Modal */}
          <LogoutConfirmationModal
            isOpen={showLogoutModal}
            onConfirm={handleConfirmedLogout}
            onCancel={handleLogoutCancel}
            ociMode={ociMode}
          />

          {/* Missing Fields Modal für interne Katalog-Produkte */}
          <MissingFieldsModal
            isOpen={showMissingFieldsModal}
            onClose={handleMissingFieldsClose}
            onConfirm={handleMissingFieldsConfirm}
            product={pendingCartProduct}
            missingFields={missingFields}
            user={user}
          />
        </>
      )}
      </div>
      
      {/* OCI In-App Browser - außerhalb des Hauptcontainers für echte Vollbild-Darstellung */}
      {showOciFrame && ociFrameUrl && (
        <OciFrame
          url={ociFrameUrl}
          onClose={closeOciFrame}
          isMobile={isMobile()}
        />
      )}
    </>
  );
};

const SAPOCICatalogApp = () => {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <ToastProvider>
          <AppContent />
        </ToastProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
};

export default SAPOCICatalogApp;